import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { styled } from "@mui/material/styles";
import { AttributeValue } from "@sinch/components/application/Worker/Attributes/AttributeValue";
import { GalleryAttribute } from "@sinch/components/application/Worker/Attributes/GalleryAttribute";
import { CustomAttributeInputBase } from "@sinch/components/application/CustomAttributeInput/CustomAttributeInputBase";
import { MdsIcon } from "@sinch/components";
import { DateInput } from "@sinch/components/form/DateInput/DateInput";
import { useMuiController } from "@sinch/components/form/MuiController/MuiController";
import { TextInput } from "@sinch/components/form/TextInput/TextInput";
import { AttributeTypeEnum, PendingChangeDecisionEnum } from "@sinch/core/entities/serverEnums";
import { MdsCheck, MdsClose, MdsEdit } from "@sinch/icons";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { GridRenderEditCellParams } from "@mui/x-data-grid-pro";
import React, { FC, MouseEvent, useCallback, useEffect, useMemo, useRef } from "react";
import { get, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PendingProfileChangeSchemaFormFields } from "../formSchema";
import { toEditableFileValue } from "../tools";
import type { WorkerPendingChangesQuery } from "../queries.types";
import { equals } from "ramda";
import { useFormat } from "@sinch/core/hooks/format";
import { ensureArray, isNotNilOrEmpty } from "ramda-adjunct";

type PendingChange = WorkerPendingChangesQuery["pendingProfileChange"][number];

export type PendingChangesRow = PendingChange & {
  ix: string | number;
  decision: PendingChangeDecisionEnum | null;
};

type DecisionFieldName = `attributes.${number}.decision`;
type NoteFieldName = `attributes.${number}.note`;
type GridCellApi = GridRenderCellParams["api"];

// Some attribute inputs emit a synthetic event-like object, others emit the raw
// value. Normalize to the raw value for the DataGrid edit-cell setter.
type EditableInputValue = string | unknown[] | { target?: { value: unknown } } | null;

const unwrapInputValue = (val: EditableInputValue): unknown => {
  if (val !== null && typeof val === "object" && "target" in val) {
    return val.target?.value;
  }
  return val;
};

const ValueCellWrapper = styled(Box)({
  width: "100%",
  "& > .MuiFormControl-root": { position: "initial" },
});

const DateCellWrapper = styled(Box)({
  width: "100%",
  "& > *": { position: "initial" },
});

const HoverInheritIcon = styled(MdsIcon)({
  "&:hover": { color: "inherit" },
});

const EditButton = styled(Button)({
  padding: 4,
  minWidth: 0,
  height: 32,
  marginRight: 16,
});

const DecisionButtonRoot = styled(Button)({
  padding: 4,
  minWidth: 0,
  width: 32,
  height: 32,
});

interface ValueDisplayCellProps {
  value: unknown;
  options: PendingChange["options"];
  type: PendingChange["dataType"];
  files?: { name: string; url: string }[] | null;
}

const ValueDisplayCell: FC<ValueDisplayCellProps> = ({ value, options, type, files }) => (
  <Box sx={{ p: 1 }}>
    <AttributeValue
      files={files}
      options={options ?? undefined}
      type={(type as AttributeTypeEnum) ?? undefined}
      value={value}
    />
  </Box>
);

// For File/Image, AttributeValue renders from `files`, not `value`. After an inline
// edit the committed `value` carries the freshly uploaded {id,url,name}, so derive
// the preview from it; fall back to the original pending files when the value is
// still the raw hash (unedited).
const resolvePendingFiles = (
  type: PendingChange["dataType"],
  value: unknown,
  fallback: { name: string; url: string }[] | null | undefined
): { name: string; url: string }[] | null | undefined => {
  if (type !== AttributeTypeEnum.File && type !== AttributeTypeEnum.Image) {
    return fallback;
  }
  const fromValue = ensureArray(value).filter(
    (file): file is { id: string; name: string; url: string } =>
      file !== null && typeof file === "object" && "url" in file
  );
  return fromValue.length > 0 ? fromValue : fallback;
};

interface GalleryPendingCellProps {
  index: number;
  editable: boolean;
  api: GridCellApi;
}

// Renders the gallery pending value with a per-file remove (X) overlay so the
// admin can drop individual images and approve the rest. Reads form state via
// useWatch (not the DataGrid row) so the cell re-renders immediately on
// removal, and writes back via setValue + auto-approve so handleReviewChanges
// sees the edit and forwards editedFileIds to the backend.
const GalleryPendingCell: FC<GalleryPendingCellProps> = ({ index, editable, api }) => {
  const { setValue } = useFormContext<PendingProfileChangeSchemaFormFields>();
  const value = useWatch<PendingProfileChangeSchemaFormFields, `attributes.${number}.value`>({
    name: `attributes.${index}.value`,
  });
  const files = (Array.isArray(value) ? value : []) as { id?: string; name: string; url: string }[];

  const handleRemove = useCallback(
    (fileId: string) => {
      const next = files.filter((file) => file.id !== fileId);
      setValue(`attributes.${index}.value`, next, { shouldDirty: true });
      setValue(`attributes.${index}.decision`, PendingChangeDecisionEnum.Approved);
      api.updateRows([{ ix: index, pendingValue: next, decision: PendingChangeDecisionEnum.Approved }]);
    },
    [files, index, setValue, api]
  );

  return (
    <Box sx={{ p: 1 }}>
      <GalleryAttribute files={files} onRemove={editable ? handleRemove : undefined} />
    </Box>
  );
};

interface ValueEditCellProps {
  row: PendingChangesRow;
  value: unknown;
  hasFocus: boolean;
  onChange: (value: unknown) => void;
}

const ValueEditCell: FC<ValueEditCellProps> = ({ row, value, hasFocus, onChange }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (hasFocus) {
      inputRef.current?.focus();
    }
  }, [hasFocus]);

  return (
    <ValueCellWrapper>
      <CustomAttributeInputBase
        attribute={{
          id: row.attributeId ?? `system/${row.fieldName}`,
          dataType: row.dataType as AttributeTypeEnum,
          options: row.options,
        }}
        disablePortal={false}
        inputRef={inputRef}
        onChange={(val: EditableInputValue) => onChange(unwrapInputValue(val))}
        placeholder={t("ProfileReview.enterCorrectValue")}
        previewSize="small"
        value={toEditableFileValue(row.dataType as AttributeTypeEnum, value, row.pendingFiles)}
      />
    </ValueCellWrapper>
  );
};

interface DecisionToggleButtonProps {
  index: number;
  decision: PendingChangeDecisionEnum;
  color: "success" | "error";
  icon: typeof MdsCheck;
  api: GridCellApi;
}

const DecisionToggleButton: FC<DecisionToggleButtonProps> = ({ index, decision, color, icon, api }) => {
  const { field } = useMuiController<PendingProfileChangeSchemaFormFields, DecisionFieldName>({
    name: `attributes.${index}.decision`,
  });

  const isActive = field.value === decision;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const next = isActive ? null : decision;
    api.updateRows([{ ix: index, decision: next }]);
    field.onChange(next);
  };

  return (
    <DecisionButtonRoot
      color={color}
      data-cy={decision === PendingChangeDecisionEnum.Approved ? "approve-row-btn" : "reject-row-btn"}
      onClick={handleClick}
      size="small"
      variant={isActive ? "contained" : "text"}
    >
      <HoverInheritIcon color={isActive ? "inherit" : "action"} icon={icon} />
    </DecisionButtonRoot>
  );
};

const ExpirationDateEdit: FC<GridRenderEditCellParams<PendingChangesRow>> = ({ id, value, api, field }) => (
  <DateInput
    disablePortal
    onChange={(val: Date | null) => api.setEditCellValue({ id, field, value: val })}
    value={value}
  />
);

interface RowActionsCellProps {
  index: number;
  api: GridCellApi;
  editable?: boolean;
}

const RowActionsCell: FC<RowActionsCellProps> = ({ index, api, editable }) => (
  <>
    {editable && (
      <EditButton
        color="info"
        onClick={() => {
          api.startRowEditMode({ id: index, fieldToFocus: "pendingValue" });
        }}
        size="small"
        variant="text"
      >
        <HoverInheritIcon color="action" icon={MdsEdit} />
      </EditButton>
    )}
    <DecisionToggleButton
      api={api}
      color="success"
      decision={PendingChangeDecisionEnum.Approved}
      icon={MdsCheck}
      index={index}
    />
    <DecisionToggleButton
      api={api}
      color="error"
      decision={PendingChangeDecisionEnum.Rejected}
      icon={MdsClose}
      index={index}
    />
  </>
);

interface RowEditActionsCellProps {
  id: string | number;
  api: GridCellApi;
}

const RowEditActionsCell: FC<RowEditActionsCellProps> = ({ id, api }) => {
  const { t } = useTranslation();
  const {
    setValue,
    getValues,
    formState: { defaultValues },
  } = useFormContext<PendingProfileChangeSchemaFormFields>();
  const { field: decisionField } = useMuiController<PendingProfileChangeSchemaFormFields, DecisionFieldName>({
    name: `attributes.${+id}.decision`,
  });
  const valueFieldName = `attributes.${+id}.value` as const;
  const expirationFieldName = `attributes.${+id}.expirationDate` as const;
  const values = getValues();
  const isRowDirty =
    !equals(get(defaultValues, valueFieldName), get(values, valueFieldName)) ||
    !equals(get(defaultValues, expirationFieldName), get(values, expirationFieldName));

  const handleRevert = () => {
    const defaultValue = get(defaultValues, valueFieldName);
    const defaultExpiration = get(defaultValues, expirationFieldName);
    // resetField() is a no-op for these fields: they are never registered as
    // inputs (only written via setValue from processRowUpdate), so the form
    // silently kept the edited value and a subsequent approve shipped it as
    // an admin edit. Write the defaults back explicitly instead.
    setValue(valueFieldName, defaultValue);
    setValue(expirationFieldName, defaultExpiration);
    decisionField.onChange(null);
    api.updateRows([{ ix: id, pendingValue: defaultValue, expirationDate: defaultExpiration, decision: null }]);
    api.stopRowEditMode({ id, ignoreModifications: true });
  };

  return (
    <>
      <Button color="info" onClick={() => api.stopRowEditMode({ id })} size="small">
        {t("apply")}
      </Button>
      {isRowDirty && (
        <Button color="warning" onClick={handleRevert} size="small">
          {t("revert")}
        </Button>
      )}
      <Button color="error" onClick={() => api.stopRowEditMode({ id, ignoreModifications: true })} size="small">
        {t("cancel")}
      </Button>
    </>
  );
};

const NoteCell: FC<{ index: number }> = ({ index }) => {
  const { t } = useTranslation();
  const { field } = useMuiController<PendingProfileChangeSchemaFormFields, NoteFieldName>({
    name: `attributes.${index}.note`,
  });

  return (
    <TextInput
      {...field}
      data-cy="row-note-input"
      // Grows in place instead of popping into a Popper on focus. The Popper
      // portals the input to the document body, which unmounts the element the
      // admin just clicked and moves it out of the cell, so focus has to be
      // handed to a brand new element mid-click. Both the grid (which focuses the
      // clicked cell from a document-level mouseup) and the dialog focus trap
      // then see the input as "outside" and pull focus away; in Firefox that
      // landed between the click and the first keystroke, so the character was
      // swallowed and the input lost focus. Rows are auto-height here, so growing
      // in place keeps the clicked element focused and needs no focus juggling.
      maxRows={4}
      multiline
      // Keep mouse and key events inside the cell: mousedown would register the
      // cell for focus on the following mouseup, and arrow keys would drive grid
      // navigation instead of moving the caret in the note.
      onKeyDown={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      placeholder={t("ProfileReview.notePlaceholder")}
    />
  );
};

// Read-mode marker for rows the admin has inline-edited. The revert button is
// only visible while the row is in edit mode, so without this the admin loses
// track of which proposed values they have already rewritten.
const AdminEditedBadge: FC<{ index: number }> = ({ index }) => {
  const { t } = useTranslation();
  const {
    formState: { defaultValues },
  } = useFormContext<PendingProfileChangeSchemaFormFields>();
  const value = useWatch<PendingProfileChangeSchemaFormFields, `attributes.${number}.value`>({
    name: `attributes.${index}.value`,
  });
  const expiration = useWatch<PendingProfileChangeSchemaFormFields, `attributes.${number}.expirationDate`>({
    name: `attributes.${index}.expirationDate`,
  });

  const isRowDirty =
    !equals(get(defaultValues, `attributes.${index}.value`), value) ||
    !equals(get(defaultValues, `attributes.${index}.expirationDate`), expiration);

  if (!isRowDirty) {
    return null;
  }

  return (
    <Chip
      color="warning"
      data-cy="row-edited-badge"
      label={t("ProfileReview.editedBadge")}
      size="small"
      sx={{ ml: 1 }}
      variant="outlined"
    />
  );
};

export const usePendingChangeColumns = (showExpiration: boolean): GridColDef<PendingChangesRow>[] => {
  const { t } = useTranslation();
  const { date } = useFormat();

  return useMemo(
    () => [
      {
        field: "attributeName",
        headerName: t("name"),
        flex: 1,
        minWidth: 180,
        editable: false,
        valueGetter: (_, row) => row.attributeName,
      },
      {
        field: "currentValue",
        headerName: t("ProfileReview.currentValue"),
        type: "custom",
        flex: 1,
        editable: false,
        renderCell: ({ value, row }) => (
          <ValueDisplayCell files={row.currentFiles} options={row.options} type={row.dataType} value={value} />
        ),
      },
      {
        field: "pendingValue",
        headerName: t("ProfileReview.proposedValue"),
        type: "custom",
        flex: 1,
        editable: true,
        renderCell: ({ value, row, api }) => (
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            {row.dataType === AttributeTypeEnum.Gallery ? (
              <GalleryPendingCell api={api} editable={row.editable} index={+row.ix} />
            ) : (
              <ValueDisplayCell
                files={resolvePendingFiles(row.dataType, value, row.pendingFiles)}
                options={row.options}
                type={row.dataType}
                value={value}
              />
            )}
            <AdminEditedBadge index={+row.ix} />
          </Box>
        ),
        renderEditCell: ({ id, row, field, api, hasFocus, value }) => (
          <ValueEditCell
            hasFocus={hasFocus}
            onChange={(next) => api.setEditCellValue({ id, field, value: next })}
            row={row}
            value={value}
          />
        ),
      },
      // The whole column is dropped when no listed attribute supports expiration -
      // it would render a wide column of dashes while long attribute names wrap.
      ...(showExpiration
        ? [
            {
              field: "expirationDate",
              headerName: t("ProfileReview.expirationDate"),
              type: "date",
              width: 150,
              align: "left",
              display: "flex",
              headerAlign: "left",
              editable: true,
              renderCell: ({ row }) =>
                isNotNilOrEmpty(row.expirationDate) ? date.mediumMonthYear(row.expirationDate) : "-",
              renderEditCell: (renderCellProps) => (
                <DateCellWrapper>
                  <ExpirationDateEdit {...renderCellProps} />
                </DateCellWrapper>
              ),
            } satisfies GridColDef<PendingChangesRow>,
          ]
        : []),
      {
        resizable: false,
        field: "decision",
        headerName: t("ProfileReview.decision"),
        type: "string",
        width: 120,
        align: "right",
        headerAlign: "right",
        display: "flex",
        maxWidth: 120,
        editable: true,
        colSpan: (_value, row, _column, apiRef) => (apiRef.current.getRowMode(row.ix) === "edit" ? 2 : undefined),
        renderEditCell: ({ id, api }) => <RowEditActionsCell api={api} id={id} />,
        renderCell: ({ row, api }) => <RowActionsCell api={api} editable={row.editable} index={+row.ix} />,
      },
      {
        cellClassName: "AttributeDataGridRow-note",
        field: "note",
        headerName: t("ProfileReview.note"),
        type: "custom",
        flex: 1,
        align: "right",
        headerAlign: "left",
        editable: false,
        renderCell: ({ row }) => <NoteCell index={+row.ix} />,
      },
    ],
    [t, date, showExpiration]
  );
};
